import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TableBody,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  ModeEdit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  BarChart as AnalyticsIcon,
  MoreHoriz as MoreVertIcon,
  CleaningServices as CleaningServicesIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SiteService } from '../../services/siteService';
import { DomainService } from '../../services/domainService';
import { LandingPage, User } from '../../types';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useUsersCache } from '../../hooks/useUsersCache';
import { getDisplayUrl, getPreviewUrl } from '../../utils/urlHelper';
import { SearchBar, DataTable, ActionButton, ActionMenu, EmptyTableRow, DetailSearchModal, ConfirmDialog, SiteTableRow, SiteTableHeader } from './common';

export const SiteList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [sites, setSites] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<LandingPage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeDomains, setActiveDomains] = useState<Record<string, string>>({});
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; site: LandingPage } | null>(null);
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();

  const [domainConfigs, setDomainConfigs] = useState<Record<string, { isActive: boolean; basePath?: string } | null>>({});
  const { usersCache } = useUsersCache(sites);
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [detailSearchOpen, setDetailSearchOpen] = useState(false);
  const [detailSearchKeyword, setDetailSearchKeyword] = useState<string>('');
  const [detailSearchStartDate, setDetailSearchStartDate] = useState<Date | null>(null);
  const [detailSearchEndDate, setDetailSearchEndDate] = useState<Date | null>(null);
  const [detailSearchStatus, setDetailSearchStatus] = useState<string>('all');
  
  // 実際にフィルタリングに使用する値
  const [appliedDetailSearchKeyword, setAppliedDetailSearchKeyword] = useState<string>('');
  const [appliedDetailSearchStartDate, setAppliedDetailSearchStartDate] = useState<Date | null>(null);
  const [appliedDetailSearchEndDate, setAppliedDetailSearchEndDate] = useState<Date | null>(null);
  const [appliedDetailSearchStatus, setAppliedDetailSearchStatus] = useState<string>('all');

  const loadActiveDomains = async (siteList: LandingPage[]) => {
    const candidates = siteList.filter(site => !!site.customDomain);
    if (candidates.length === 0) {
      setActiveDomains({});
      setDomainConfigs({});
      return;
    }

    try {
      const results = await Promise.all(
        candidates.map(async (site) => {
          try {
            const config = await DomainService.getDomain(site.id);
            return {
              siteId: site.id,
              domain: config && config.isActive ? config.domain : null,
              domainConfig: config ? { isActive: config.isActive, basePath: config.basePath } : null,
            };
          } catch (error) {
            console.error('ドメイン状態の取得に失敗しました:', error);
            return { siteId: site.id, domain: null, domainConfig: null };
          }
        })
      );

      const domainMap: Record<string, string> = {};
      const configMap: Record<string, { isActive: boolean } | null> = {};
      results.forEach(({ siteId, domain, domainConfig }) => {
        if (domain) {
          domainMap[siteId] = domain;
        }
        configMap[siteId] = domainConfig;
      });
      setActiveDomains(domainMap);
      setDomainConfigs(configMap);
    } catch (error) {
      console.error('ドメイン情報の読み込みに失敗しました:', error);
      setActiveDomains({});
      setDomainConfigs({});
    }
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!currentUser) {
        setSites([]);
        setActiveDomains({});
        return;
      }
      
      const sitesData = await SiteService.getSites();
      
      let filteredSitesData = sitesData;
      if (currentUser?.role === 'viewer') {
        filteredSitesData = sitesData.filter(site => site.companyUserId === currentUser.id);
      }
      
      setSites(filteredSitesData);
      await loadActiveDomains(filteredSitesData);
    } catch (err) {
      console.error('サイト取得エラー:', err);
      setError(err instanceof Error ? err.message : 'サイトの取得に失敗しました');
      setSites([]);
      setActiveDomains({});
    } finally {
      setLoading(false);
    }
  };

  const deleteSite = async (siteId: string) => {
    try {
      await SiteService.deleteSite(siteId);
      // 削除後に一覧を再取得
      await fetchSites();
    } catch (err) {
      console.error('サイトの削除に失敗しました:', err);
      throw err;
    }
  };

  const displayUser = React.useMemo(() => {
    return currentUser || {
      id: 'guest',
      displayName: 'ゲストユーザー',
      email: 'guest@example.com'
    };
  }, [currentUser]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      // 同じフィールドの場合は昇順・降順を切り替え
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 異なるフィールドの場合は新しいフィールドで昇順に設定
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredSites = useMemo(() => {
    let filtered = sites;

    if (activeTab === 1) {
      filtered = sites.filter(site => {
        const domainConfig = domainConfigs[site.id];
        return SiteService.getActualStatusSync(site, domainConfig) === 'published';
      });
    } else if (activeTab === 2) {
      filtered = sites.filter(site => {
        const domainConfig = domainConfigs[site.id];
        return SiteService.getActualStatusSync(site, domainConfig) === 'unpublished';
      });
    } else if (activeTab === 3) {
      filtered = sites.filter(site => {
        const domainConfig = domainConfigs[site.id];
        return SiteService.getActualStatusSync(site, domainConfig) === 'draft';
      });
    }

    if (searchKeyword.trim()) {
      filtered = filtered.filter(site => 
        site.title?.toLowerCase().includes(searchKeyword.toLowerCase())
      );
    }

    if (appliedDetailSearchKeyword.trim()) {
      filtered = filtered.filter(site => {
        const titleMatch = site.title?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase());
        const companyUser = site.companyUserId ? usersCache[site.companyUserId] : null;
        const companyNameMatch = companyUser?.displayName?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase());
        const updaterUserId = site.updatedByUserId || site.createdByUserId;
        const updaterUser = updaterUserId ? usersCache[updaterUserId] : null;
        const userNameMatch = updaterUser?.displayName?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase()) ||
                             updaterUser?.email?.toLowerCase().includes(appliedDetailSearchKeyword.toLowerCase());
        return titleMatch || companyNameMatch || userNameMatch;
      });
    }

    if (appliedDetailSearchStatus && appliedDetailSearchStatus !== 'all') {
      filtered = filtered.filter(site => {
        const domainConfig = domainConfigs[site.id];
        const actualStatus = SiteService.getActualStatusSync(site, domainConfig);
        return actualStatus === appliedDetailSearchStatus;
      });
    }

    if (appliedDetailSearchStartDate || appliedDetailSearchEndDate) {
      filtered = filtered.filter(site => {
        const dateToCheck = site.createdAt;
        
        if (!dateToCheck) return false;

        const checkDate = new Date(dateToCheck);
        checkDate.setHours(0, 0, 0, 0);

        if (appliedDetailSearchStartDate) {
          const startDate = new Date(appliedDetailSearchStartDate);
          startDate.setHours(0, 0, 0, 0);
          if (checkDate < startDate) return false;
        }

        if (appliedDetailSearchEndDate) {
          const endDate = new Date(appliedDetailSearchEndDate);
          endDate.setHours(23, 59, 59, 999);
          if (checkDate > endDate) return false;
        }

        return true;
      });
    }

    if (sortField) {
      const sorted = [...filtered].sort((a, b) => {
        const domainConfigA = domainConfigs[a.id];
        const domainConfigB = domainConfigs[b.id];
        const actualStatusA = SiteService.getActualStatusSync(a, domainConfigA);
        const actualStatusB = SiteService.getActualStatusSync(b, domainConfigB);

        let valueA: any;
        let valueB: any;

        switch (sortField) {
          case 'status':
            const statusOrder: Record<string, number> = { 'published': 1, 'unpublished': 2, 'draft': 3 };
            valueA = statusOrder[actualStatusA] || 999;
            valueB = statusOrder[actualStatusB] || 999;
            break;
          case 'publishedDate':
            valueA = actualStatusA === 'published' ? (a.updatedAt || a.createdAt)?.getTime() || 0 : 0;
            valueB = actualStatusB === 'published' ? (b.updatedAt || b.createdAt)?.getTime() || 0 : 0;
            break;
          case 'title':
            valueA = a.title || '';
            valueB = b.title || '';
            break;
          case 'updater':
            const updaterA = (a.updatedByUserId || a.createdByUserId) ? usersCache[a.updatedByUserId || a.createdByUserId || ''] : null;
            const updaterB = (b.updatedByUserId || b.createdByUserId) ? usersCache[b.updatedByUserId || b.createdByUserId || ''] : null;
            valueA = updaterA ? (updaterA.displayName || updaterA.email || '') : '';
            valueB = updaterB ? (updaterB.displayName || updaterB.email || '') : '';
            break;
          case 'updatedAt':
            valueA = (a.updatedAt || a.createdAt)?.getTime() || 0;
            valueB = (b.updatedAt || b.createdAt)?.getTime() || 0;
            break;
          case 'companyName':
            const companyA = a.companyUserId ? usersCache[a.companyUserId] : null;
            const companyB = b.companyUserId ? usersCache[b.companyUserId] : null;
            valueA = companyA ? (companyA.displayName || '') : '';
            valueB = companyB ? (companyB.displayName || '') : '';
            break;
          default:
            return 0;
        }

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortDirection === 'asc' 
            ? valueA.localeCompare(valueB, 'ja')
            : valueB.localeCompare(valueA, 'ja');
        } else {
          return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
        }
      });
      filtered = sorted;
    }

    return filtered;
  }, [sites, activeTab, domainConfigs, sortField, sortDirection, usersCache, searchKeyword, appliedDetailSearchKeyword, appliedDetailSearchStatus, appliedDetailSearchStartDate, appliedDetailSearchEndDate]);


  const handleCreateSite = () => {
    navigate('/admin/sites/create');
  };

  const handleEditSite = (site: LandingPage) => {
    navigate(`/admin/sites/${site.id}/edit`);
  };

  const handlePreviewSite = (site: LandingPage) => {
    const basePath = domainConfigs[site.id]?.basePath;
    const previewUrl = activeDomains[site.id]
      ? getDisplayUrl(activeDomains[site.id], basePath)
      : getPreviewUrl(site.id);

    window.open(previewUrl, '_blank');
    handleMenuClose();
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, site: LandingPage) => {
    setMenuAnchor({ element: event.currentTarget, site });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };
  
  const handleMenuDelete = (site: LandingPage) => {
    setSelectedSite(site);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleClearCache = async (site: LandingPage) => {
    handleMenuClose();
    
    if (!window.confirm('Cloudflareのキャッシュをクリアしますか？\nサイトの表示が最新の状態に更新されます。')) {
      return;
    }

    try {
      setActionLoading(true);
      const domainConfig = await DomainService.getDomain(site.id);
      if (!domainConfig) {
        showSnackbar('ドメイン設定が見つかりません', 'error');
        return;
      }
      await DomainService.purgeCache(domainConfig.zoneId);
      showSnackbar('✅ キャッシュを正常にクリアしました', 'success');
    } catch (error: any) {
      showSnackbar(error.message || 'キャッシュクリアに失敗しました', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAnalyticsSite = (site: LandingPage) => {
    navigate(`/admin/sites/${site.id}/analytics`);
  };

  const confirmDeleteSite = async () => {
    if (selectedSite) {
      try {
        setActionLoading(true);
        await deleteSite(selectedSite.id);
      } catch (error) {
      } finally {
        setActionLoading(false);
      }
    }
    setDeleteDialogOpen(false);
    setSelectedSite(null);
  };

  const handleDetailSearchOpen = () => {
    setDetailSearchOpen(true);
  };

  const handleDetailSearchClose = () => {
    setDetailSearchOpen(false);
  };

  const handleDetailSearch = () => {
    setAppliedDetailSearchKeyword(detailSearchKeyword);
    setAppliedDetailSearchStartDate(detailSearchStartDate);
    setAppliedDetailSearchEndDate(detailSearchEndDate);
    setAppliedDetailSearchStatus(detailSearchStatus);
    setDetailSearchOpen(false);
  };

  const handleDetailSearchReset = () => {
    setDetailSearchKeyword('');
    setDetailSearchStartDate(null);
    setDetailSearchEndDate(null);
    setDetailSearchStatus('all');
    setAppliedDetailSearchKeyword('');
    setAppliedDetailSearchStartDate(null);
    setAppliedDetailSearchEndDate(null);
    setAppliedDetailSearchStatus('all');
  };

  useEffect(() => {
    fetchSites();
  }, [currentUser]);

  useEffect(() => {
    if (location.state?.message) {
      showSnackbar(location.state.message, location.state.severity || 'success');
      // メッセージを表示したらstateをクリア
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          再読み込み
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={4}>
        <Typography 
          variant="h4" 
          component="h1"
            sx={{ fontSize: '32px', fontWeight: 600, color: '#000000' }}
        >
          サイト管理
        </Typography>
          <SearchBar
              value={searchKeyword}
            onChange={setSearchKeyword}
            borderRadius="16px"
            height="48px"
            fontSize="14px"
            onFilterClick={handleDetailSearchOpen}
          />
        </Box>
        {currentUser?.role !== 'viewer' && (
          <IconButton
            onClick={handleCreateSite}
            sx={{
              background: '#1B76D2 0% 0% no-repeat padding-box',
              borderRadius: '16px',
              color: '#FFFFFF',
              width: 56,
              height: 56,
              '&:hover': {
                backgroundColor: '#1565C0',
              },
            }}
          >
            <AddIcon sx={{ fontSize: '28px', fontWeight: 900, transform: 'scale(1.1)' }} />
          </IconButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#1B76D2',
              height: 2,
            },
          }}
        >
          <Tab 
            label={`全て (${sites.length})`}
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1B76D2',
              },
            }}
          />
          <Tab 
            label={`公開 (${sites.filter(site => {
              const domainConfig = domainConfigs[site.id];
              return SiteService.getActualStatusSync(site, domainConfig) === 'published';
            }).length})`}
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1B76D2',
              },
            }}
          />
          <Tab 
            label={`非公開 (${sites.filter(site => {
              const domainConfig = domainConfigs[site.id];
              return SiteService.getActualStatusSync(site, domainConfig) === 'unpublished';
            }).length})`}
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1B76D2',
              },
            }}
          />
          <Tab 
            label={`下書き (${sites.filter(site => {
              const domainConfig = domainConfigs[site.id];
              return SiteService.getActualStatusSync(site, domainConfig) === 'draft';
            }).length})`}
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#1B76D2',
              },
            }}
          />
        </Tabs>
      </Box>

      <DataTable>
        <SiteTableHeader
          sortable={true}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        <TableBody>
          {filteredSites.length === 0 ? (
            <EmptyTableRow colSpan={9} message="サイトがありません" />
          ) : (
            filteredSites.map((site) => (
              <SiteTableRow
                key={site.id}
                site={site}
                domainConfig={domainConfigs[site.id]}
                activeDomain={activeDomains[site.id]}
                usersCache={usersCache}
                currentUser={currentUser}
                onEdit={handleEditSite}
                onAnalytics={handleAnalyticsSite}
                onMenuOpen={handleMenuOpen}
                showEditButton={currentUser?.role !== 'viewer'}
              />
            ))
          )}
        </TableBody>
      </DataTable>

      {currentUser?.role !== 'viewer' && (
        <ActionMenu
          anchorEl={menuAnchor?.element || null}
          open={!!menuAnchor}
          onClose={handleMenuClose}
          items={[
            {
              label: 'プレビュー',
              icon: <ViewIcon sx={{ fontSize: '18px', color: '#616161' }} />,
              onClick: () => menuAnchor && handlePreviewSite(menuAnchor.site),
            },
            {
              label: 'キャッシュクリア',
              icon: <CleaningServicesIcon sx={{ fontSize: '18px', color: '#616161' }} />,
              onClick: () => menuAnchor && handleClearCache(menuAnchor.site),
            },
            {
              label: '削除',
              icon: <DeleteIcon sx={{ fontSize: '18px', color: '#D32F2F' }} />,
              onClick: () => menuAnchor && handleMenuDelete(menuAnchor.site),
              color: '#D32F2F',
            },
          ]}
        />
      )}


      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedSite(null);
        }}
        onConfirm={confirmDeleteSite}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 詳細検索モーダル */}
      <DetailSearchModal
        open={detailSearchOpen}
        onClose={handleDetailSearchClose}
        onSearch={handleDetailSearch}
        keyword={detailSearchKeyword}
        onKeywordChange={setDetailSearchKeyword}
        keywordLabel="キーワード (サイト名 / クライアント名 / ユーザー名)"
        keywordPlaceholder="キーワードを入力"
        startDate={detailSearchStartDate}
        onStartDateChange={setDetailSearchStartDate}
        endDate={detailSearchEndDate}
        onEndDateChange={setDetailSearchEndDate}
        selectLabel="ステータス"
        selectValue={detailSearchStatus}
        onSelectChange={setDetailSearchStatus}
        selectOptions={[
          { value: 'all', label: 'すべて' },
          { value: 'published', label: '公開中' },
          { value: 'unpublished', label: '非公開' },
          { value: 'draft', label: '下書き' },
        ]}
      />
    </Box>
  );
};
